import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  makeStyles,
  TextField,
  MenuItem,
  InputAdornment,
} from '@material-ui/core';
import { blueBar } from '../../../helpers/common';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '500px',
    overflow: 'scroll',
    overflowX: 'hidden',
    '@media (max-width: 920px)': {
      height: '70%',
    },
    '&::-webkit-scrollbar': {
      background: 'white',
      borderStyle: 'solid',
      borderWidth: '1px',
      borderColor: '#beb1b18f',
      width: '9px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#dfdfe957',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.primary.main,
    },
    '& .MuiFormControl-root': {
      marginBottom: theme.spacing(2),
    },
  },
}));

const clamp = (val, min, max) => {
  if (val === '' || val === null || val === undefined) return val;
  const n = Number(val);
  if (Number.isNaN(n)) return val;
  return Math.max(min, Math.min(max, n));
};

const toNum = (v, fallback = undefined) => {
  if (v === '' || v === null || v === undefined) return fallback;
  const n = Number(v);
  return Number.isNaN(n) ? fallback : n;
};

// modo: 0 auto/auto | 1 auto/fijo | 2 fijo/auto | 3 fijo/fijo
const modeRules = {
  0: ['lat', 'lon', 'm', 'sro', 'sso'],
  1: ['lat', 'lon', 'm', 'sro', 'fss'],
  2: ['lat', 'lon', 'm', 'fsr', 'sso'],
  3: ['lat', 'lon', 'm', 'fsr', 'fss'],
};

const isSalidaAuto = (m) => m === 0 || m === 1;
const isPuestaAuto = (m) => m === 0 || m === 2;
const noValidateKeys = new Set(['sro', 'sso']);


const offsetLegend = (tipo) =>
  tipo === 'sro'
    ? 'Antes / después de la salida del sol'
    : 'Antes / después de la puesta del sol';

const formatOffsetHelper = (v, tipo) => {
  const legend = offsetLegend(tipo);

  const n = Number(v);
  if (v === '' || v === null || v === undefined || Number.isNaN(n)) {
    return legend;
  }

  const base =
    tipo === 'sro' ? 'de la salida del sol calculada' : 'de la puesta del sol calculada';

  if (n === 0) return `Exacto (${base})`;
  if (n < 0) return `${Math.abs(n)} min antes ${base}`;
  return `${n} min después ${base}`;
};

const floatToTimeString = (valFloat) => {
  if (valFloat === '' || valFloat === null || valFloat === undefined) return '';
  const n = Number(valFloat);
  if (Number.isNaN(n)) return '';

  const hh = Math.floor(n);
  let mm = Math.round((n - hh) * 60);

  let safeH = hh;
  if (mm >= 60) {
    safeH += 1;
    mm = 0;
  }

  if (safeH < 0) safeH = 0;
  if (safeH > 23) safeH = 23;

  return `${String(safeH).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

const timeStringToFloat = (time) => {
  if (!time || typeof time !== 'string') return '';
  const [hh, mm] = time.split(':');
  const H = Number(hh);
  const M = Number(mm);
  if (Number.isNaN(H) || Number.isNaN(M)) return '';
  return Number((H + M / 60).toFixed(4));
};

const TastroFilterOptions = ({
  varState,
  setVarState,
  setDisabledButtonSave,
  blockNodeSettings,
}) => {
  const classes = useStyles();
  const blueLine = blueBar();

  const initialData = blockNodeSettings.atributes_keys.reduce((acc, attr) => {
    acc[attr.key] = attr.default ?? '';
    return acc;
  }, {});

  const [formData, setFormData] = useState({
    ...initialData,
    m: 0,
  });

  const [timeDrafts, setTimeDrafts] = useState({
    fsr: '',
    fss: '',
  });

  useEffect(() => {
    if (varState?.a) {
      setFormData((prev) => ({
        ...prev,
        ...varState.a,
        m: toNum(varState.a.m, 0),
        fsr: toNum(varState.a.fsr),
        fss: toNum(varState.a.fss),
        sro: varState.a.sro ?? prev.sro,
        sso: varState.a.sso ?? prev.sso,
      }));
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setTimeDrafts({
      fsr: floatToTimeString(formData.fsr),
      fss: floatToTimeString(formData.fss),
    });
  }, [formData.fsr, formData.fss]);


  const mode = useMemo(() => Number(formData.m) || 0, [formData.m]);
  const visibleKeys = modeRules[mode] || [];

  const errors = useMemo(() => {
    const errs = {};
    visibleKeys.forEach((key) => {
      if (noValidateKeys.has(key)) return;

      const rules = blockNodeSettings.pins[key];
      const val = formData[key];

      if (val === '' || val === undefined) {
        errs[key] = true;
        return;
      }

      if (rules?.method === 'INTERVAL') {
        const n = Number(val);
        if (Number.isNaN(n) || n < rules.rule[0] || n > rules.rule[1]) {
          errs[key] = true;
        }
      }
    });
    return errs;
  }, [formData, visibleKeys, blockNodeSettings.pins]);

  const formatAsHHMM = (raw) => {
    const digits = String(raw).replace(/\D/g, '').slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  };

  const isValidPartialOrFullHHMM = (value) => {
    if (value === '') return true;
    if (/^\d{1}$/.test(value)) return true;
    if (/^\d{2}$/.test(value)) return Number(value) <= 23;
    if (/^\d{2}:$/.test(value)) return Number(value.slice(0, 2)) <= 23;
    if (/^\d{2}:\d{1}$/.test(value)) {
      const [hh, mm1] = value.split(':');
      return Number(hh) <= 23 && Number(mm1) <= 5;
    }
    if (/^\d{2}:\d{2}$/.test(value)) {
      const [hh, mm] = value.split(':');
      const h = Number(hh);
      const m = Number(mm);
      return h >= 0 && h <= 23 && m >= 0 && m <= 59;
    }
    return false;
  };

  const isValidFullHHMM = (value) =>
    /^\d{2}:\d{2}$/.test(value) && isValidPartialOrFullHHMM(value);


  const buildAttributes = (fd) => {
    const a = {};

    visibleKeys.forEach((key) => {
      if (fd[key] !== '' && fd[key] !== undefined) {
        a[key] = toNum(fd[key]);
      }
    });

    // Salida
    if (isSalidaAuto(mode)) a.fsr = 0;
    else a.sro = 0;

    // Puesta
    if (isPuestaAuto(mode)) a.fss = 0;
    else a.sso = 0;

    return a;
  };

  useEffect(() => {
    setVarState((prev) => ({
      ...prev,
      a: buildAttributes(formData),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, setVarState]);

  useEffect(() => {
    const hasError = Object.values(errors).some((e) => e === true);
    setDisabledButtonSave?.((prev) => (prev !== hasError ? hasError : prev));
  }, [errors, setDisabledButtonSave]);

  const handleChange = (field, opts) => (e) => {
    const raw = e.target.value;
    let value = raw;

    if (opts?.clamp) {
      value = clamp(raw, opts.clamp[0], opts.clamp[1]);
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const renderNumberField = ({
    key,
    label,
    helper,
    adornment,
    clampWithPins = true,
    clampOverride,
    hideRange = false,
  }) => {
    const rules = blockNodeSettings.pins[key];
    const typeDef = blockNodeSettings.validation_types[key]?.[0];
    const isNumber = typeDef === 'float' || typeDef === 'uint' || typeDef === 'int';

    const clampRule =
      clampOverride
        ? clampOverride
        : (clampWithPins && rules?.method === 'INTERVAL' ? rules.rule : null);

    const showRange =
      hideRange
        ? null
        : clampOverride
          ? `Rango válido: ${clampOverride[0]} a ${clampOverride[1]}`
          : (rules?.method === 'INTERVAL'
            ? `Rango válido: ${rules.rule[0]} a ${rules.rule[1]}`
            : null);

    return (
      <TextField
        key={key}
        label={label}
        variant="outlined"
        size="small"
        fullWidth
        type={isNumber ? 'number' : 'text'}
        inputProps={{
          min: clampRule?.[0],
          max: clampRule?.[1],
          step: 1,
        }}
        InputProps={
          adornment
            ? { endAdornment: <InputAdornment position="end">{adornment}</InputAdornment> }
            : undefined
        }
        value={formData[key]}
        onChange={handleChange(key, clampRule ? { clamp: clampRule } : undefined)}
        error={false}
        helperText={showRange || helper || ' '}
      />
    );
  };

  const renderFixedTime = (key, label) => {
    const floatVal = formData[key];
    const draft = timeDrafts[key] ?? floatToTimeString(floatVal);

    const setDraft = (next) =>
      setTimeDrafts((prev) => ({ ...prev, [key]: next }));

    return (
      <TextField
        key={key}
        label={label}
        variant="outlined"
        size="small"
        fullWidth
        type="text"
        value={draft}
        placeholder="HH:mm"
        inputProps={{ inputMode: 'numeric', maxLength: 5 }}
        onChange={(e) => {
          const formatted = formatAsHHMM(e.target.value);

          if (!isValidPartialOrFullHHMM(formatted)) return;

          setDraft(formatted);

          if (isValidFullHHMM(formatted)) {
            const nextFloat = timeStringToFloat(formatted);
            setFormData((prev) => ({ ...prev, [key]: nextFloat }));
          }
        }}
        onBlur={() => {
          if (isValidFullHHMM(draft)) {
            const nextFloat = timeStringToFloat(draft);
            setFormData((prev) => ({ ...prev, [key]: nextFloat }));
          } else {
            setDraft(floatToTimeString(floatVal));
          }
        }}
        InputLabelProps={{ shrink: true }}
        error={!!errors[key]}
        helperText="Formato 24hs (00:00–23:59)"
      />
    );
  };

  const specialKeys = new Set(['fsr', 'fss', 'sro', 'sso']);

  return (
    <Box className={classes.root}>
      {blueLine}

      <Box width="100%">
        {blockNodeSettings.atributes_keys.map((attr) => {
          if (!visibleKeys.includes(attr.key)) return null;

          if (attr.key === 'm') {
            return (
              <TextField
                key={attr.key}
                select
                label={attr.name}
                variant="outlined"
                size="small"
                fullWidth
                value={formData.m}
                onChange={handleChange('m')}
              >
                <MenuItem value={0}>0 - Salida Auto / Puesta Auto</MenuItem>
                <MenuItem value={1}>1 - Salida Auto / Puesta Fija</MenuItem>
                <MenuItem value={2}>2 - Salida Fija / Puesta Auto</MenuItem>
                <MenuItem value={3}>3 - Salida Fija / Puesta Fija</MenuItem>
              </TextField>
            );
          }

          if (specialKeys.has(attr.key)) return null;

          const rules = blockNodeSettings.pins[attr.key];
          const type = blockNodeSettings.validation_types[attr.key]?.[0];

          return (
            <TextField
              key={attr.key}
              label={attr.name}
              variant="outlined"
              size="small"
              fullWidth
              type={type === 'float' || type === 'uint' ? 'number' : 'text'}
              inputProps={{
                min: rules?.rule?.[0],
                max: rules?.rule?.[1],
                step: type === 'float' ? 0.1 : 1,
              }}
              value={formData[attr.key]}
              onChange={handleChange(
                attr.key,
                rules?.method === 'INTERVAL' ? { clamp: rules.rule } : undefined
              )}
              error={!!errors[attr.key]}
              helperText={
                errors[attr.key] && rules
                  ? `Rango válido: ${rules.rule[0]} a ${rules.rule[1]}`
                  : ' '
              }
            />
          );
        })}

        {/* BLOQUE ESPECIAL: Salida */}
        {(() => {
          const salidaAuto = isSalidaAuto(mode);

          if (salidaAuto) {
            return renderNumberField({
              key: 'sro',
              label: 'Salida - Offset (min)',
              helper: formatOffsetHelper(formData.sro, 'sro'),
              adornment: 'min',
              clampWithPins: false,
              clampOverride: [-360, 360],
              hideRange: true,
            });
          }

          return renderFixedTime('fsr', 'Salida - Hora fija');
        })()}

        {/* BLOQUE ESPECIAL: Puesta */}
        {(() => {
          const puestaAuto = isPuestaAuto(mode);

          if (puestaAuto) {
            return renderNumberField({
              key: 'sso',
              label: 'Puesta - Offset (min)',
              helper: formatOffsetHelper(formData.sso, 'sso'),
              adornment: 'min',
              clampWithPins: false,
              clampOverride: [-360, 360],
              hideRange: true,
            });
          }

          return renderFixedTime('fss', 'Puesta - Hora fija');
        })()}
      </Box>
    </Box>
  );
};

export default TastroFilterOptions;
